import React, { useEffect, useState } from "react";
import { TrendingUp, Target, Gauge } from "lucide-react";
import { api } from "../lib/api";

const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
const fmtGbp = (v) =>
  v == null ? "—" : `£${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const IRR_STATUS = {
  on_track: { label: "On track", cls: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  at_risk: { label: "At risk", cls: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  below_floor: { label: "Below 10% floor", cls: "bg-red-100 text-red-800", dot: "bg-red-500" },
  unknown: { label: "No actuals yet", cls: "bg-zinc-100 text-zinc-600", dot: "bg-zinc-400" },
};

export default function FinancialKpis({ projectId, data }) {
  const [fin, setFin] = useState(data || null);
  const [loading, setLoading] = useState(!data);

  useEffect(() => {
    if (data !== undefined && data !== null) { setFin(data); return; }
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    api.get(`/monitoring/${projectId}/financial-kpis`)
      .then((r) => { if (alive) setFin(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, data]);

  if (loading) {
    return (
      <section className="swiss-card-flat p-6" data-testid="financial-kpis-loading">
        <div className="label">Financial yield &amp; IRR tracking</div>
        <div className="muted text-sm mt-2">Loading financial KPIs…</div>
      </section>
    );
  }
  if (!fin) return null;

  const yoc = fin.yield_on_cost || {};
  const irr = fin.irr || {};
  const st = IRR_STATUS[irr.status] || IRR_STATUS.unknown;
  const projTone = irr.status === "on_track" ? "text-emerald-700"
    : irr.status === "at_risk" ? "text-amber-700"
    : irr.status === "below_floor" ? "text-red-600" : "text-[var(--ink)]";

  return (
    <section className="swiss-card-flat p-6" data-testid="financial-kpis">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="label">Financial yield &amp; IRR tracking</div>
        {!fin.has_actuals && (
          <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700"
                data-testid="fin-no-actuals">
            Design only · no production income yet
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-1 bg-[var(--surface-2)]">
        {/* Yield on Cost */}
        <div className="bg-white p-5 border border-[var(--line)]" data-testid="fin-yield-on-cost">
          <div className="label flex items-center gap-1.5"><TrendingUp size={13} /> Yield on Cost</div>
          <div className="font-display text-2xl font-bold mt-1 font-mono">{fmtPct(yoc.actual_pct)}</div>
          <div className="text-xs muted mt-1">
            design {fmtPct(yoc.design_pct)} · net ÷ {fmtGbp(fin.capex_gbp)} CAPEX
            {fin.capex_basis === "actual_invoiced" ? " (actual)" : ""}
          </div>
        </div>

        {/* IRR target */}
        <div className="bg-white p-5 border border-[var(--line)]" data-testid="fin-irr-target">
          <div className="label flex items-center gap-1.5"><Target size={13} /> IRR — design target</div>
          <div className="font-display text-2xl font-bold mt-1 font-mono">{fmtPct(irr.design_target_pct)}</div>
          <div className="text-xs muted mt-1">{fmtPct(irr.floor_pct)} hard floor</div>
        </div>

        {/* IRR projected */}
        <div className="bg-white p-5 border border-[var(--line)]" data-testid="fin-irr-projected">
          <div className="label flex items-center gap-1.5"><Gauge size={13} /> IRR — projected</div>
          <div className={`font-display text-2xl font-bold mt-1 font-mono ${projTone}`}>
            {fmtPct(irr.projected_pct)}
          </div>
          <div className="text-xs muted mt-1">
            {irr.performance_factor != null ? `at ×${irr.performance_factor} of plan` : "needs actuals"}
          </div>
        </div>

        {/* Pathway status */}
        <div className="bg-[var(--ink)] text-white p-5 border border-[var(--line)] flex flex-col justify-center"
             data-testid="fin-irr-status">
          <div className="label !text-white/70">Pathway status</div>
          <div className="mt-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.cls}`}>
              <span className={`w-2 h-2 rounded-full ${st.dot}`} /> {st.label}
            </span>
          </div>
          <div className="text-xs text-white/70 mt-2">
            {fin.annual_net_income_gbp != null ? `${fmtGbp(fin.annual_net_income_gbp)} annual net income` : "—"}
          </div>
        </div>
      </div>
    </section>
  );
}
